import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase, NewsPost, I18nText } from '../../../lib/supabase';
import WysiwygEditor from './WysiwygEditor';

type Lang = 'ko' | 'en' | 'vi';

const LANGS: { key: Lang; label: string }[] = [
  { key: 'ko', label: '한국어' },
  { key: 'en', label: 'English' },
  { key: 'vi', label: 'Tiếng Việt' },
];

interface PostI18n {
  title: string;
  summary: string;
  content: string;
}

const emptyI18n = (): PostI18n => ({ title: '', summary: '', content: '' });

interface Props {
  showMsg: (text: string) => void;
}

export default function NewsEditor({ showMsg }: Props) {
  const [posts, setPosts] = useState<NewsPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<Partial<NewsPost> | null>(null);
  const [activeLang, setActiveLang] = useState<Lang>('ko');
  const [i18nData, setI18nData] = useState<Record<Lang, PostI18n>>({
    ko: emptyI18n(),
    en: emptyI18n(),
    vi: emptyI18n(),
  });
  const [tagsText, setTagsText] = useState('');
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverPreview, setCoverPreview] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('news_posts')
      .select('*')
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: false });
    setPosts((data as NewsPost[]) || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const openNew = () => {
    setEditing({
      slug: '',
      cover_image_url: '',
      tags: [],
      author: '',
      is_published: false,
      published_at: null,
      sort_order: 0,
    });
    setI18nData({ ko: emptyI18n(), en: emptyI18n(), vi: emptyI18n() });
    setTagsText('');
    setCoverPreview('');
    setActiveLang('ko');
  };

  const openEdit = (post: NewsPost) => {
    setEditing(post);
    const ti = (post.title_i18n as I18nText) || {};
    const si = (post.summary_i18n as I18nText) || {};
    const ci = (post.content_i18n as I18nText) || {};
    setI18nData({
      ko: { title: ti.ko || '', summary: si.ko || '', content: ci.ko || '' },
      en: { title: ti.en || '', summary: si.en || '', content: ci.en || '' },
      vi: { title: ti.vi || '', summary: si.vi || '', content: ci.vi || '' },
    });
    setTagsText((post.tags || []).join(', '));
    setCoverPreview(post.cover_image_url || '');
    setActiveLang('ko');
  };

  const updateI18n = (lang: Lang, field: keyof PostI18n, value: string) => {
    setI18nData((prev) => ({ ...prev, [lang]: { ...prev[lang], [field]: value } }));
  };

  const handleCoverUpload = async (file: File) => {
    setCoverUploading(true);
    const ext = file.name.split('.').pop();
    const fileName = `news/${Date.now()}.${ext}`;
    const { data, error } = await supabase.storage
      .from('service-images')
      .upload(fileName, file, { upsert: true });
    if (error) {
      showMsg('이미지 업로드 실패: ' + error.message);
      setCoverUploading(false);
      return;
    }
    const { data: { publicUrl } } = supabase.storage.from('service-images').getPublicUrl(data.path);
    setEditing((prev) => prev ? { ...prev, cover_image_url: publicUrl } : prev);
    setCoverPreview(publicUrl);
    setCoverUploading(false);
    showMsg('커버 이미지 업로드 완료!');
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCoverPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
    handleCoverUpload(file);
  };

  const generateSlug = () => {
    const title = i18nData.ko.title || i18nData.en.title;
    if (!title) return;
    const slug = title
      .toLowerCase()
      .replace(/[^a-z0-9가-힣ㄱ-ㅎ ]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 80)
      + '-' + Date.now().toString().slice(-5);
    setEditing((prev) => prev ? { ...prev, slug } : prev);
  };

  const savePost = async () => {
    if (!editing) return;
    if (!editing.slug) { showMsg('슬러그(URL)를 입력해주세요.'); return; }
    if (!i18nData.ko.title && !i18nData.en.title) { showMsg('제목을 입력해주세요.'); return; }

    setSaving(true);
    const tags = tagsText.split(',').map((t) => t.trim()).filter(Boolean);
    const payload = {
      slug: editing.slug,
      cover_image_url: editing.cover_image_url || '',
      tags,
      author: editing.author || '',
      is_published: editing.is_published ?? false,
      published_at: editing.is_published
        ? (editing.published_at || new Date().toISOString())
        : editing.published_at,
      sort_order: editing.sort_order ?? 0,
      title_i18n: { ko: i18nData.ko.title, en: i18nData.en.title, vi: i18nData.vi.title },
      summary_i18n: { ko: i18nData.ko.summary, en: i18nData.en.summary, vi: i18nData.vi.summary },
      content_i18n: { ko: i18nData.ko.content, en: i18nData.en.content, vi: i18nData.vi.content },
    };

    if ((editing as NewsPost).id) {
      const { error } = await supabase
        .from('news_posts')
        .update(payload)
        .eq('id', (editing as NewsPost).id);
      if (error) { showMsg('저장 실패: ' + error.message); setSaving(false); return; }
    } else {
      const { error } = await supabase.from('news_posts').insert(payload);
      if (error) { showMsg('저장 실패: ' + error.message); setSaving(false); return; }
    }

    setEditing(null);
    await fetchPosts();
    showMsg('포스팅이 저장됐어요!');
    setSaving(false);
  };

  const deletePost = async (id: number) => {
    if (!confirm('포스팅을 삭제할까요?')) return;
    await supabase.from('news_posts').delete().eq('id', id);
    await fetchPosts();
    showMsg('삭제됐어요.');
  };

  const togglePublish = async (post: NewsPost) => {
    const newVal = !post.is_published;
    await supabase
      .from('news_posts')
      .update({
        is_published: newVal,
        published_at: newVal ? (post.published_at || new Date().toISOString()) : post.published_at,
      })
      .eq('id', post.id);
    await fetchPosts();
    showMsg(newVal ? '게시됐어요!' : '비공개로 변경됐어요.');
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-serif text-navy text-xl font-bold">소식 포스팅 관리</h2>
        <button
          onClick={openNew}
          className="bg-navy text-white text-xs font-sans font-semibold px-4 py-2.5 rounded-lg flex items-center gap-1.5 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line" /> 포스팅 작성
        </button>
      </div>

      {loading ? (
        <div className="text-center py-16 text-navy/30 font-sans text-sm">불러오는 중...</div>
      ) : posts.length === 0 ? (
        <div className="text-center py-16">
          <i className="ri-newspaper-line text-4xl text-navy/20 block mb-3" />
          <p className="text-navy/30 font-sans text-sm">아직 포스팅이 없어요. 위 버튼으로 작성해 보세요!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((post) => {
            const title = (post.title_i18n as I18nText)?.ko || (post.title_i18n as I18nText)?.en || '(제목 없음)';
            const dateStr = post.published_at
              ? new Date(post.published_at).toLocaleDateString('ko-KR', { year: 'numeric', month: 'short', day: 'numeric' })
              : '-';
            return (
              <div
                key={post.id}
                className="bg-white rounded-xl border border-gray-100 px-5 py-4 flex items-center gap-4"
              >
                {/* Cover thumbnail */}
                <div className="w-16 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-[#F8F9FB]">
                  {post.cover_image_url ? (
                    <img src={post.cover_image_url} alt={title} className="w-full h-full object-cover object-top" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <i className="ri-image-line text-navy/20 text-lg" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-navy text-sm font-sans font-semibold truncate">{title}</p>
                    <span className={`px-2 py-0.5 text-[10px] font-sans font-semibold rounded-full whitespace-nowrap ${
                      post.is_published ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-navy/40'
                    }`}>
                      {post.is_published ? '게시됨' : '비공개'}
                    </span>
                  </div>
                  <p className="text-navy/40 text-xs font-sans">
                    <span className="font-mono text-navy/30">/news/{post.slug}</span>
                    {post.tags?.length ? ` · ${post.tags.slice(0, 2).join(', ')}` : ''}
                    {dateStr !== '-' ? ` · ${dateStr}` : ''}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => togglePublish(post)}
                    title={post.is_published ? '비공개로 전환' : '게시하기'}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                      post.is_published ? 'hover:bg-amber-50' : 'hover:bg-green-50'
                    }`}
                  >
                    <i className={`text-sm ${post.is_published ? 'ri-eye-off-line text-amber-400' : 'ri-eye-line text-green-500'}`} />
                  </button>
                  <button
                    onClick={() => openEdit(post)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#F8F9FB] transition-colors cursor-pointer"
                  >
                    <i className="ri-edit-line text-navy/50 text-sm" />
                  </button>
                  <button
                    onClick={() => deletePost(post.id)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-delete-bin-line text-red-400 text-sm" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Edit Modal ── */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 px-4 overflow-y-auto py-6">
          <div className="bg-white rounded-2xl w-full max-w-2xl my-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-8 pt-7 pb-5 border-b border-gray-100">
              <h3 className="font-serif text-navy text-lg font-bold">
                {(editing as NewsPost).id ? '포스팅 수정' : '새 포스팅 작성'}
              </h3>
              <button
                onClick={() => setEditing(null)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-navy/50" />
              </button>
            </div>

            <div className="px-8 py-6 space-y-5 max-h-[75vh] overflow-y-auto">
              {/* Cover Image */}
              <div>
                <label className="block text-xs font-sans text-navy/50 mb-2">커버 이미지</label>
                {coverPreview ? (
                  <div className="relative w-full h-44 rounded-xl overflow-hidden mb-3 bg-[#F8F9FB]">
                    <img src={coverPreview} alt="cover" className="w-full h-full object-cover object-top" />
                    <button
                      onClick={() => { setCoverPreview(''); setEditing((p) => p ? { ...p, cover_image_url: '' } : p); }}
                      className="absolute top-2 right-2 w-7 h-7 bg-white/90 rounded-full flex items-center justify-center cursor-pointer hover:bg-white transition-colors"
                    >
                      <i className="ri-close-line text-navy text-sm" />
                    </button>
                  </div>
                ) : (
                  <div className="w-full h-32 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center mb-3 bg-[#F8F9FB]">
                    <div className="text-center">
                      <i className="ri-image-add-line text-navy/20 text-2xl block mb-1" />
                      <p className="text-xs text-navy/30 font-sans">이미지 없음</p>
                    </div>
                  </div>
                )}
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onFileChange} className="hidden" />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={coverUploading}
                  className="w-full border border-gray-200 rounded-lg py-2.5 text-sm font-sans text-navy/60 hover:border-navy hover:text-navy transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {coverUploading ? <><i className="ri-loader-4-line animate-spin" /> 업로드 중...</> : <><i className="ri-upload-cloud-line" /> {coverPreview ? '이미지 교체' : '커버 이미지 업로드'}</>}
                </button>
              </div>

              {/* Slug (URL) */}
              <div>
                <label className="block text-xs font-sans text-navy/50 mb-1.5">
                  URL 슬러그 <span className="text-navy/30">(도메인: /news/슬러그)</span>
                </label>
                <div className="flex gap-2">
                  <div className="flex-1 flex items-center border border-gray-200 rounded-lg overflow-hidden focus-within:border-navy">
                    <span className="px-3 text-xs text-navy/30 font-sans bg-[#F8F9FB] h-full flex items-center border-r border-gray-200 whitespace-nowrap py-2.5">/news/</span>
                    <input
                      value={editing.slug || ''}
                      onChange={(e) => setEditing((p) => p ? { ...p, slug: e.target.value.replace(/[^a-z0-9\-]/g, '') } : p)}
                      placeholder="my-post-url"
                      className="flex-1 px-3 py-2.5 text-sm font-sans font-mono outline-none"
                    />
                  </div>
                  <button
                    onClick={generateSlug}
                    className="px-3 py-2.5 border border-gray-200 rounded-lg text-xs font-sans text-navy/60 hover:border-navy hover:text-navy transition-all cursor-pointer whitespace-nowrap"
                    title="제목에서 자동 생성"
                  >
                    <i className="ri-magic-line mr-1" />자동
                  </button>
                </div>
              </div>

              {/* Language Tabs */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-xs font-sans text-navy/50">콘텐츠 언어</label>
                  <div className="flex gap-1">
                    {LANGS.map((l) => (
                      <button
                        key={l.key}
                        onClick={() => setActiveLang(l.key)}
                        className={`px-3 py-1 rounded text-xs font-sans cursor-pointer whitespace-nowrap transition-all ${
                          activeLang === l.key ? 'bg-navy text-white' : 'bg-gray-100 text-navy/50 hover:bg-gray-200'
                        }`}
                      >
                        {l.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="border border-gray-100 rounded-xl p-5 bg-[#F8F9FB] space-y-4">
                  {/* Title */}
                  <div>
                    <label className="block text-xs font-sans text-navy/50 mb-1.5">제목</label>
                    <input
                      value={i18nData[activeLang].title}
                      onChange={(e) => updateI18n(activeLang, 'title', e.target.value)}
                      placeholder={`제목 (${activeLang === 'ko' ? '한국어' : activeLang === 'en' ? 'English' : 'Tiếng Việt'})`}
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-sans outline-none focus:border-navy bg-white"
                    />
                  </div>

                  {/* Summary */}
                  <div>
                    <label className="block text-xs font-sans text-navy/50 mb-1.5">요약 (카드에 표시)</label>
                    <textarea
                      value={i18nData[activeLang].summary}
                      onChange={(e) => updateI18n(activeLang, 'summary', e.target.value)}
                      rows={2}
                      maxLength={300}
                      placeholder="한 두 줄 요약..."
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-sans outline-none focus:border-navy resize-none bg-white"
                    />
                  </div>

                  {/* Content — WYSIWYG */}
                  <div>
                    <label className="block text-xs font-sans text-navy/50 mb-1.5">본문</label>
                    <WysiwygEditor
                      value={i18nData[activeLang].content}
                      onChange={(html) => updateI18n(activeLang, 'content', html)}
                      editorKey={activeLang}
                      placeholder={`본문 내용을 입력하세요 (${activeLang === 'ko' ? '한국어' : activeLang === 'en' ? 'English' : 'Tiếng Việt'})`}
                      minHeight={280}
                    />
                  </div>
                </div>
              </div>

              {/* Tags + Author + Sort */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-sans text-navy/50 mb-1.5">태그 (쉼표 구분)</label>
                  <input
                    value={tagsText}
                    onChange={(e) => setTagsText(e.target.value)}
                    placeholder="무역, 한국, 베트남"
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-sans outline-none focus:border-navy"
                  />
                </div>
                <div>
                  <label className="block text-xs font-sans text-navy/50 mb-1.5">작성자</label>
                  <input
                    value={editing.author || ''}
                    onChange={(e) => setEditing((p) => p ? { ...p, author: e.target.value } : p)}
                    placeholder="MONARU Team"
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-sans outline-none focus:border-navy"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-sans text-navy/50 mb-1.5">게시 날짜</label>
                  <input
                    type="date"
                    value={editing.published_at ? editing.published_at.slice(0, 10) : ''}
                    onChange={(e) => setEditing((p) => p ? { ...p, published_at: e.target.value ? new Date(e.target.value).toISOString() : null } : p)}
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-sans outline-none focus:border-navy cursor-pointer"
                  />
                </div>
                <div>
                  <label className="block text-xs font-sans text-navy/50 mb-1.5">정렬 순서</label>
                  <input
                    type="number"
                    value={editing.sort_order ?? 0}
                    onChange={(e) => setEditing((p) => p ? { ...p, sort_order: Number(e.target.value) } : p)}
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-sans outline-none focus:border-navy"
                  />
                </div>
              </div>

              {/* Publish toggle */}
              <div className="flex items-center justify-between p-4 bg-[#F8F9FB] rounded-xl border border-gray-100">
                <div>
                  <p className="text-sm font-sans font-semibold text-navy">게시 상태</p>
                  <p className="text-xs font-sans text-navy/40 mt-0.5">
                    {editing.is_published ? '공개 — 소식 페이지에 표시됩니다' : '비공개 — 소식 페이지에 숨겨집니다'}
                  </p>
                </div>
                <button
                  onClick={() => setEditing((p) => p ? { ...p, is_published: !p.is_published } : p)}
                  className={`relative w-12 h-6 rounded-full transition-colors duration-200 cursor-pointer flex-shrink-0 ${
                    editing.is_published ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 ${
                    editing.is_published ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="px-8 pb-7 pt-4 flex gap-3 border-t border-gray-100">
              <button
                onClick={() => setEditing(null)}
                className="flex-1 border border-gray-200 text-navy/50 text-sm font-sans py-3 rounded-lg hover:bg-[#F8F9FB] transition-colors cursor-pointer whitespace-nowrap"
              >
                취소
              </button>
              <button
                onClick={savePost}
                disabled={saving || coverUploading}
                className="flex-1 bg-navy text-white text-sm font-sans font-semibold py-3 rounded-lg hover:bg-navy/90 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
